import { ExternalLink, Eye, Pencil } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { fmtDate } from '../../../shared/format'
import type { CatalogModuleView, ProjectSummary } from '../../types'
import { MODULE_CATEGORY_ICON, MODULE_CATEGORY_TONE } from '../../visuals'
import { ProjectIcon } from '../ProjectBits'
import { usePriceFormat } from '../../usePriceFormat'

type Props = { module: CatalogModuleView; projects: ProjectSummary[]; canWrite: boolean; onEdit: () => void; onClose: () => void }

export function ModulePriceDetailModal({ module: m, projects, canWrite, onEdit, onClose }: Props) {
  const { t } = useTranslation()
  const fmtPrice = usePriceFormat()
  const Icon = MODULE_CATEGORY_ICON[m.category]
  const offered = m.usage.length

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onClose()}
      icon={<Eye />}
      title={t('projects.catalog.priceDetail.title', { name: m.name })}
      description={t('projects.catalog.priceDetail.subtitle')}
      footer={
        <>
          <Button variant="outline" asChild>
            <Link to={`/projects/catalog/${m.id}`}>
              <ExternalLink />
              {t('projects.catalog.priceDetail.openModule')}
            </Link>
          </Button>
          {canWrite ? (
            <Button onClick={onEdit}>
              <Pencil />
              {t('projects.catalog.priceList.edit')}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-lg border bg-muted/30 p-3">
          <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-lg', MODULE_CATEGORY_TONE[m.category])}>
            <Icon className="size-5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold">{m.name}</span>
              <Badge tone={m.active ? 'success' : 'neutral'}>{m.active ? t('projects.catalog.activeLabel') : t('projects.catalog.priceList.inactive')}</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              {m.code} · {t(`projects.moduleCategory.${m.category}`)} · {t(`projects.pricing.${m.pricing}`)}
            </p>
          </div>
        </div>

        <dl className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border p-3">
            <dt className="text-xs text-muted-foreground">{t('projects.catalog.priceList.listPrice')}</dt>
            <dd className="text-lg font-semibold tabular-nums">
              {fmtPrice(m.price)}
              {m.pricing !== 'one_time' ? <span className="text-xs font-normal text-muted-foreground"> {t(`projects.pricingSuffix.${m.pricing}`)}</span> : null}
            </dd>
          </div>
          <div className="rounded-lg border p-3">
            <dt className="text-xs text-muted-foreground">{t('projects.catalog.priceDetail.offeredIn')}</dt>
            <dd className="text-lg font-semibold tabular-nums">{t('projects.catalog.priceDetail.offeredCount', { count: offered, total: projects.length })}</dd>
          </div>
        </dl>

        <div className="overflow-hidden rounded-lg border">
          <Table>
            <THead>
              <TR className="hover:bg-transparent">
                <TH>{t('projects.catalog.priceDetail.product')}</TH>
                <TH>{t('projects.catalog.priceDetail.status')}</TH>
                <TH className="text-right">{t('projects.catalog.priceDetail.price')}</TH>
                <TH className="text-right">{t('projects.catalog.detail.added')}</TH>
              </TR>
            </THead>
            <TBody>
              {projects.map((p) => {
                const u = m.usage.find((x) => x.projectId === p.id)
                return (
                  <TR key={p.id} className={cn(!u && 'text-muted-foreground')}>
                    <TD>
                      <span className="inline-flex items-center gap-2">
                        <ProjectIcon icon={p.icon} color={p.color} className="size-6 rounded-md shadow-none" />
                        <span className="font-medium">{p.name}</span>
                      </span>
                    </TD>
                    <TD>
                      {u ? (
                        <Badge tone={u.requirement === 'required' ? 'success' : 'info'}>{t(`projects.requirement.${u.requirement}`)}</Badge>
                      ) : (
                        <span className="text-xs">{t('projects.catalog.pricing.notOffered')}</span>
                      )}
                    </TD>
                    <TD className="text-right">
                      {u ? (
                        <>
                          <span className="tabular-nums">{fmtPrice(u.price)}</span>
                          {u.price !== m.price ? <span className="block text-[11px] text-warning-strong">{t('projects.catalog.priceDetail.special')}</span> : null}
                        </>
                      ) : (
                        '—'
                      )}
                    </TD>
                    <TD className="whitespace-nowrap text-right text-sm tabular-nums">{u ? fmtDate(u.addedAt) : '—'}</TD>
                  </TR>
                )
              })}
            </TBody>
          </Table>
        </div>
      </div>
    </Modal>
  )
}
